import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { RadioButton } from "primereact/radiobutton";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import MeaButton from "../../../../components/MeaButton";
import MeaStudentResult from "../../../../components/MeaStudentResult";
import studentsAPI from "../../../../services/students";

export const STUDENT_RESULT_OPTIONS = [
  { value: "ONGOING", label: "En cours", icon: "pi pi-play", description: "La décision de passage n'est pas encore prise." },
  { value: "PASSED", label: "Passage", icon: "pi pi-check-circle", description: "L'élève passe au niveau supérieur." },
  { value: "FAILED", label: "Redoublement", icon: "pi pi-exclamation-triangle", description: "L'élève redouble son niveau." },
];

const UpdateStudentResultModal = forwardRef(({ student, schoolId, trainingId }, ref) => {
  const toast = useRef(null);
  const [visible, setVisible] = useState(false);
  const [result, setResult] = useState(null);
  const [appreciation, setAppreciation] = useState("");
  const [loading, setLoading] = useState(false);
  const [callbackFn, setCallbackFn] = useState(() => () => {});

  useImperativeHandle(ref, () => ({
    show: (callback) => {
      setResult(student?.result || null);
      setAppreciation(student?.result_observation || "");
      setCallbackFn(() => callback);
      setVisible(true);
    },
    hide: () => setVisible(false),
  }));

  const resultChanged = !!result && result !== student?.result;
  const appreciationChanged = appreciation !== (student?.result_observation || "");

  const handleSave = async () => {
    setLoading(true);
    try {
      await studentsAPI.UpdateResult(
        schoolId,
        trainingId,
        "",
        student?.id,
        resultChanged ? result : null,
        appreciationChanged ? appreciation : null
      );
      toast.current?.show({ severity: "success", summary: "Succès", life: 1500, detail: "Avis de passage et appréciation mis à jour." });
      setVisible(false);
      callbackFn?.();
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Erreur", detail: "Erreur de modification de l'avis de passage !" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toast ref={toast} position="bottom-center" />
      <Dialog
        header="Avis de passage & appréciation générale"
        visible={visible}
        style={{ width: "40vw" }}
        breakpoints={{ "960px": "75vw", "641px": "100vw" }}
        modal
        onHide={() => !loading && setVisible(false)}
        footer={
          <div className="flex justify-content-end gap-2">
            <MeaButton label="Annuler" icon="pi pi-times" className="p-button-text" disabled={loading} onClick={() => setVisible(false)} />
            <MeaButton
              label="Enregistrer"
              icon="pi pi-save"
              loading={loading}
              disabled={!resultChanged && !appreciationChanged}
              onClick={handleSave}
            />
          </div>
        }
      >
        <div className="flex align-items-center gap-2 mb-3 text-sm">
          <span className="text-500">Avis actuel :</span>
          <MeaStudentResult student={student} />
        </div>
        <div className="flex flex-column gap-2">
          {STUDENT_RESULT_OPTIONS.map((option) => {
            const inputId = `student-result-${option.value}`;
            return (
              <div
                key={option.value}
                className={`flex align-items-start gap-3 p-3 border-1 border-round cursor-pointer ${result === option.value ? "border-primary surface-100" : "surface-border"}`}
                onClick={() => setResult(option.value)}
              >
                <RadioButton inputId={inputId} name="student-result" value={option.value} checked={result === option.value} onChange={(e) => setResult(e.value)} />
                <label htmlFor={inputId} className="flex-1 cursor-pointer">
                  <span className="font-medium"><i className={`${option.icon} mr-2`} />{option.label}</span>
                  <small className="block text-500 mt-1">{option.description}</small>
                </label>
              </div>
            );
          })}
        </div>
        <div className="flex flex-column gap-2 mt-4">
          <label htmlFor="student-result-appreciation" className="font-medium">Appréciation générale</label>
          <InputTextarea
            id="student-result-appreciation"
            value={appreciation}
            onChange={(e) => setAppreciation(e.target.value)}
            rows={5}
            autoResize
            placeholder="Suivi et appréciation générale de l'élève..."
          />
        </div>
      </Dialog>
    </>
  );
});

export default UpdateStudentResultModal;
